import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '@flowcore-water/ui';
import { QUESTION_TOPICS, type QuestionTopic, type Question } from '../../types';
import { flashcardService, questionService } from '../../services/questionBankService';
import type { FlashcardRecord } from '../../types';

interface AnswerState {
  questionId: string;
  selectedChoice: string | null;
  answered: boolean;
  correct: boolean;
}

export const Flashcards: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTheme();

  // Filter state
  const [selectedTopics, setSelectedTopics] = useState<QuestionTopic[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string[]>([
    'basic',
    'intermediate',
    'advanced',
  ]);
  const [mathOnly, setMathOnly] = useState(false);

  // Questions loaded from service
  const [questionBank, setQuestionBank] = useState<Question[]>([]);

  // Flashcard state
  const [records, setRecords] = useState<Record<string, FlashcardRecord>>({});
  const [answerState, setAnswerState] = useState<AnswerState | null>(null);
  const [loading, setLoading] = useState(true);

  // Session ("trip") stats — resets with the Reset button
  const [sessionStats, setSessionStats] = useState({ seen: 0, correct: 0, streak: 0 });

  // Deck management: we build the deck only on filter changes or explicit reshuffle,
  // NOT when records change (which would shuffle mid-question).
  const [deck, setDeck] = useState<string[]>([]); // array of question IDs
  const [deckIndex, setDeckIndex] = useState(0);
  const deckVersion = useRef(0); // bumped on filter change to trigger rebuild

  // Load questions and flashcard records on mount
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      const [questions, flashcards] = await Promise.all([
        questionService.getAll(),
        flashcardService.getRecords(user.uid),
      ]);
      setQuestionBank(questions);
      setRecords(flashcards);
      setLoading(false);
    };
    loadData();
  }, [user]);

  // Build deck when filters change or records first load
  const buildDeck = useCallback(
    (recs: Record<string, FlashcardRecord>) => {
      let questions = questionBank.filter((q) => {
        const topicsMatch =
          selectedTopics.length === 0 || selectedTopics.includes(q.topic);
        const difficultyMatch = selectedDifficulty.includes(q.difficulty);
        const mathMatch = !mathOnly || q.isMath;
        return topicsMatch && difficultyMatch && mathMatch;
      });

      // Sort: unseen first, then most-missed, then random
      questions.sort((a, b) => {
        const aRec = recs[a.id];
        const bRec = recs[b.id];
        const aUnseen = !aRec || (aRec.correct === 0 && aRec.incorrect === 0);
        const bUnseen = !bRec || (bRec.correct === 0 && bRec.incorrect === 0);
        if (aUnseen !== bUnseen) return aUnseen ? -1 : 1;
        const aInc = aRec?.incorrect || 0;
        const bInc = bRec?.incorrect || 0;
        if (aInc !== bInc) return bInc - aInc;
        return Math.random() - 0.5;
      });

      return questions.map((q) => q.id);
    },
    [selectedTopics, selectedDifficulty, mathOnly]
  );

  // Rebuild deck when filters change
  useEffect(() => {
    if (loading) return;
    deckVersion.current++;
    const newDeck = buildDeck(records);
    setDeck(newDeck);
    setDeckIndex(0);
    setAnswerState(null);
  }, [selectedTopics, selectedDifficulty, mathOnly, loading]); // intentionally NOT records

  // Also build initial deck once records load
  const initialBuildDone = useRef(false);
  useEffect(() => {
    if (!loading && !initialBuildDone.current) {
      initialBuildDone.current = true;
      const newDeck = buildDeck(records);
      setDeck(newDeck);
    }
  }, [loading, records, buildDeck]);

  // Current question from deck
  const currentQuestion = useMemo(() => {
    const qId = deck[deckIndex];
    return qId ? questionBank.find((q) => q.id === qId) || null : null;
  }, [deck, deckIndex]);

  // All-time stats from records
  const allTimeStats = useMemo(() => {
    let correct = 0;
    let incorrect = 0;
    let bestStreak = 0;
    Object.values(records).forEach((r) => {
      correct += r.correct;
      incorrect += r.incorrect;
      if (r.streak > bestStreak) bestStreak = r.streak;
    });
    const total = correct + incorrect;
    return { total, correct, incorrect, bestStreak, accuracy: total > 0 ? Math.round((correct / total) * 100) : 0 };
  }, [records]);

  // Topic accuracy from records
  const topicAccuracy = useMemo(() => {
    const accuracy: Record<QuestionTopic, { correct: number; total: number }> = {} as any;
    QUESTION_TOPICS.forEach((topic) => {
      accuracy[topic.id] = { correct: 0, total: 0 };
    });
    questionBank.forEach((q) => {
      const record = records[q.id];
      if (record) {
        accuracy[q.topic].total += record.correct + record.incorrect;
        accuracy[q.topic].correct += record.correct;
      }
    });
    return accuracy;
  }, [records]);

  // Handle answer selection — does NOT trigger deck rebuild
  const handleChoiceClick = async (choiceId: string) => {
    if (!currentQuestion || answerState?.answered) return;

    const isCorrect = choiceId === currentQuestion.correctAnswer;
    setAnswerState({
      questionId: currentQuestion.id,
      selectedChoice: choiceId,
      answered: true,
      correct: isCorrect,
    });

    // Update session stats
    setSessionStats((prev) => ({
      seen: prev.seen + 1,
      correct: prev.correct + (isCorrect ? 1 : 0),
      streak: isCorrect ? prev.streak + 1 : 0,
    }));

    // Persist record
    if (user) {
      const currentRecord = records[currentQuestion.id] || {
        questionId: currentQuestion.id,
        correct: 0,
        incorrect: 0,
        lastSeen: new Date(),
        streak: 0,
      };

      const updated: FlashcardRecord = {
        questionId: currentQuestion.id,
        correct: isCorrect ? currentRecord.correct + 1 : currentRecord.correct,
        incorrect: isCorrect ? currentRecord.incorrect : currentRecord.incorrect + 1,
        lastSeen: new Date(),
        streak: isCorrect ? currentRecord.streak + 1 : 0,
      };

      // Update records state (won't rebuild deck because deck doesn't depend on records)
      setRecords((prev) => ({ ...prev, [currentQuestion.id]: updated }));
      await flashcardService.saveRecord(user.uid, updated);
    }
  };

  // Handle next
  const handleNext = () => {
    if (deckIndex < deck.length - 1) {
      setDeckIndex(deckIndex + 1);
    } else {
      // Reshuffle and restart
      const newDeck = buildDeck(records);
      setDeck(newDeck);
      setDeckIndex(0);
    }
    setAnswerState(null);
  };

  const toggleTopic = (topicId: QuestionTopic) => {
    setSelectedTopics((prev) =>
      prev.includes(topicId) ? prev.filter((ti) => ti !== topicId) : [...prev, topicId]
    );
  };

  const toggleDifficulty = (difficulty: string) => {
    setSelectedDifficulty((prev) =>
      prev.includes(difficulty) ? prev.filter((d) => d !== difficulty) : [...prev, difficulty]
    );
  };

  const handleResetSession = () => {
    setSessionStats({ seen: 0, correct: 0, streak: 0 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div style={{ color: t.textSecondary }}>Loading flashcards...</div>
      </div>
    );
  }

  const sessionAccuracy =
    sessionStats.seen > 0 ? Math.round((sessionStats.correct / sessionStats.seen) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 style={{ color: t.textPrimary }} className="text-2xl font-bold mb-1">
          Study Flashcards
        </h1>
        <p style={{ color: t.textSecondary }} className="text-sm">
          {questionBank.length} questions across {QUESTION_TOPICS.length} topics
        </p>
      </div>

      {/* Stats: All-Time (odometer) + Session (trip) */}
      <div
        style={{ backgroundColor: t.cardBg, border: `1px solid ${t.border}` }}
        className="rounded-lg p-4 mb-6"
      >
        <div className="flex items-center justify-between mb-3">
          <div style={{ color: t.textMuted }} className="text-xs font-semibold tracking-widest uppercase">
            All-Time
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <div style={{ color: t.textMuted }} className="text-xs mb-0.5">ANSWERED</div>
            <div style={{ color: t.accent }} className="text-xl font-bold font-mono">{allTimeStats.total}</div>
          </div>
          <div>
            <div style={{ color: t.textMuted }} className="text-xs mb-0.5">ACCURACY</div>
            <div style={{ color: allTimeStats.accuracy >= 70 ? t.success : t.fail }} className="text-xl font-bold font-mono">
              {allTimeStats.accuracy}%
            </div>
          </div>
          <div>
            <div style={{ color: t.textMuted }} className="text-xs mb-0.5">CORRECT</div>
            <div style={{ color: t.success }} className="text-xl font-bold font-mono">{allTimeStats.correct}</div>
          </div>
          <div>
            <div style={{ color: t.textMuted }} className="text-xs mb-0.5">BEST STREAK</div>
            <div style={{ color: t.accent }} className="text-xl font-bold font-mono">{allTimeStats.bestStreak}</div>
          </div>
        </div>

        <div
          style={{ borderTop: `1px solid ${t.border}` }}
          className="pt-3"
        >
          <div className="flex items-center justify-between mb-3">
            <div style={{ color: t.textMuted }} className="text-xs font-semibold tracking-widest uppercase">
              This Session
            </div>
            <button
              onClick={handleResetSession}
              style={{ color: t.textMuted, border: `1px solid ${t.border}` }}
              className="text-xs px-2 py-0.5 rounded hover:opacity-80 transition-opacity"
            >
              Reset
            </button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div style={{ color: t.textMuted }} className="text-xs mb-0.5">SEEN</div>
              <div style={{ color: t.accent }} className="text-xl font-bold font-mono">{sessionStats.seen}</div>
            </div>
            <div>
              <div style={{ color: t.textMuted }} className="text-xs mb-0.5">ACCURACY</div>
              <div style={{ color: sessionAccuracy >= 70 ? t.success : sessionStats.seen > 0 ? t.fail : t.textMuted }} className="text-xl font-bold font-mono">
                {sessionStats.seen > 0 ? `${sessionAccuracy}%` : '—'}
              </div>
            </div>
            <div>
              <div style={{ color: t.textMuted }} className="text-xs mb-0.5">STREAK</div>
              <div style={{ color: t.accent }} className="text-xl font-bold font-mono">{sessionStats.streak}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Topic Filter Bar */}
      <div
        style={{ backgroundColor: t.cardBg, border: `1px solid ${t.border}` }}
        className="rounded-lg p-4 mb-6"
      >
        <h3 style={{ color: t.textPrimary }} className="text-sm font-semibold mb-3">
          FILTER BY TOPIC
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {QUESTION_TOPICS.map((topic) => {
            const accuracy = topicAccuracy[topic.id];
            const percent =
              accuracy.total > 0 ? Math.round((accuracy.correct / accuracy.total) * 100) : 0;
            const isSelected = selectedTopics.includes(topic.id);

            return (
              <button
                key={topic.id}
                onClick={() => toggleTopic(topic.id)}
                style={{
                  backgroundColor: isSelected ? t.accentBg : 'transparent',
                  border: `1px solid ${isSelected ? t.accent : t.border}`,
                  color: isSelected ? t.accent : t.textSecondary,
                }}
                className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
              >
                {topic.label}
                {accuracy.total > 0 && (
                  <span style={{ opacity: 0.7 }} className="ml-1">
                    ({percent}%)
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Difficulty Filter */}
        <div className="mb-3">
          <div style={{ color: t.textMuted }} className="text-xs font-medium mb-2">
            DIFFICULTY
          </div>
          <div className="flex gap-2">
            {['basic', 'intermediate', 'advanced'].map((difficulty) => (
              <button
                key={difficulty}
                onClick={() => toggleDifficulty(difficulty)}
                style={{
                  backgroundColor: selectedDifficulty.includes(difficulty)
                    ? t.accentBg
                    : 'transparent',
                  border: `1px solid ${selectedDifficulty.includes(difficulty) ? t.accent : t.border}`,
                  color: selectedDifficulty.includes(difficulty) ? t.accent : t.textSecondary,
                }}
                className="px-2.5 py-1 rounded text-xs font-medium transition-colors capitalize"
              >
                {difficulty}
              </button>
            ))}
          </div>
        </div>

        {/* Math Only Toggle */}
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={mathOnly}
            onChange={(e) => setMathOnly(e.target.checked)}
            className="w-4 h-4 rounded"
            style={{ accentColor: t.accent }}
          />
          <span style={{ color: t.textSecondary }} className="ml-2 text-xs font-medium">
            Math Only
          </span>
        </label>
      </div>

      {/* Flashcard */}
      {deck.length > 0 && currentQuestion ? (
        <div className="mb-6">
          <div
            style={{ backgroundColor: t.cardBg, border: `1px solid ${t.border}` }}
            className="rounded-lg p-6 min-h-96 flex flex-col"
          >
            {/* Question Counter + Topic Badge */}
            <div className="flex items-center justify-between mb-4">
              <div style={{ color: t.textMuted }} className="text-xs font-medium">
                {deckIndex + 1} of {deck.length}
              </div>
              <div className="flex gap-2">
                <span
                  style={{ color: t.textMuted, border: `1px solid ${t.border}` }}
                  className="text-xs px-2 py-0.5 rounded capitalize"
                >
                  {currentQuestion.difficulty}
                </span>
                <span
                  style={{ color: t.accent, border: `1px solid ${t.accent}40` }}
                  className="text-xs px-2 py-0.5 rounded"
                >
                  {QUESTION_TOPICS.find((tp) => tp.id === currentQuestion.topic)?.label}
                </span>
              </div>
            </div>

            {/* Question Text */}
            <div className="mb-6 flex-grow">
              <h2 style={{ color: t.textPrimary }} className="text-lg font-semibold mb-4">
                {currentQuestion.question}
              </h2>

              {/* Choices */}
              <div className="space-y-2">
                {currentQuestion.choices.map((choice) => {
                  const isSelected = answerState?.selectedChoice === choice.id;
                  const isCorrectAnswer = choice.id === currentQuestion.correctAnswer;
                  const isWrongSelection = isSelected && !isCorrectAnswer;
                  const isCorrectSelection = isSelected && isCorrectAnswer;

                  let bgColor = t.surfaceHover;
                  let borderColor = t.border;
                  let textColor = t.textPrimary;

                  if (answerState?.answered) {
                    if (isCorrectSelection) {
                      bgColor = t.successBg;
                      borderColor = t.successBorder;
                      textColor = t.success;
                    } else if (isWrongSelection) {
                      bgColor = t.failBg;
                      borderColor = t.failBorder;
                      textColor = t.fail;
                    } else if (isCorrectAnswer) {
                      bgColor = t.successBg;
                      borderColor = t.successBorder;
                      textColor = t.success;
                    }
                  }

                  return (
                    <button
                      key={choice.id}
                      onClick={() => handleChoiceClick(choice.id)}
                      disabled={answerState?.answered}
                      style={{
                        backgroundColor: bgColor,
                        border: `1px solid ${borderColor}`,
                        color: textColor,
                      }}
                      className="w-full text-left p-3 rounded transition-colors disabled:cursor-default"
                    >
                      <span className="font-semibold">
                        {choice.id.toUpperCase()}.
                      </span>
                      <span className="ml-3">{choice.text}</span>
                      {answerState?.answered && isCorrectAnswer && (
                        <span className="ml-2 text-sm">✓</span>
                      )}
                      {answerState?.answered && isWrongSelection && (
                        <span className="ml-2 text-sm">✗</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              {answerState?.answered && (
                <div
                  style={{
                    backgroundColor: t.accentBg,
                    border: `1px solid ${t.border}`,
                    color: t.textSecondary,
                  }}
                  className="mt-4 p-3 rounded text-sm"
                >
                  <div style={{ color: t.accent }} className="font-semibold mb-1">
                    Explanation
                  </div>
                  {currentQuestion.explanation}
                </div>
              )}
            </div>

            {/* Next Button */}
            {answerState?.answered && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleNext}
                  style={{ backgroundColor: t.accent, color: '#000' }}
                  className="px-4 py-2 rounded font-semibold transition-opacity hover:opacity-90"
                >
                  {deckIndex < deck.length - 1 ? 'Next Card' : 'Start Over'}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          style={{ backgroundColor: t.cardBg, border: `1px solid ${t.border}` }}
          className="rounded-lg p-6 text-center"
        >
          <p style={{ color: t.textSecondary }}>No questions match your filters.</p>
        </div>
      )}
    </div>
  );
};

export default Flashcards;
